/* eslint-disable react-hooks/exhaustive-deps */

import * as React from "react"
import { useHistory } from "react-router-dom"
import { Box, Flex } from "@chakra-ui/react"

import Shows from "../shows"
import { MediaPanel } from "../../mediaPanel"
import { ServicesPanel } from "../../servicesPanel"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import {
  brandingSelector,
  isShowsGridViewSupportedSelector,
  roomSelector
} from "../../../recoil/atoms/room"
import SmallView from "../../mediaPanel/small-view"
import useWindowSize from "../../../hooks/useWindowSize"
import layoutStates from "../../../recoil/atoms/layout"
import { detectDeviceType, isIpad } from "../../../util/deviceType"
import {
  MODAL_Z_INDEX,
  RemoteVideoWindowsLimit,
  USE_MOBILE_UI,
  SHOW_DESKTOP_TAB_INDEX
} from "../../../constants"
import {
  localUserIdAtom,
  usersSelector,
  remoteUsersListSelector
} from "../../../recoil/atoms/users"
import * as Util from "../../../util"
import {
  watchPartyScreenSelector,
  watchPartyScreenLinkSelector,
  watchPartyHostIdSelector
} from "../../../recoil/atoms/watchPartyScreen"
import { screenShareUserSelector } from "../../../recoil/atoms/screenShareUser"
import { activeSpeakerSelector } from "../../../recoil/atoms/activeSpeaker"
import { mainWindowUserSelector } from "../../../recoil/atoms/mainWindowUser"
import { SDKContext } from "../../../context/SDKContext"
import { usersListSmallWindowSelector } from "../../../recoil/atoms/userListSmallWindow"
import fullscreenState from "../../../recoil/atoms/fullscreen"
import HandleChatCount from "../../servicesPanel/chat/HandleChatCount"
import LSRTCMediaConnection from "../../mediaPanel/videoPlayer/LSRTCMediaConnection"
import { stopClientExpandingAtom } from "../../../recoil/atoms/layout/stopClientExpanding"
import PrivateRoomHandler from "../../mediaPanel/privateRoom/PrivateRoomHandler"
import useNotifyUserAboutStreamQuality from "../../../hooks/useNotifyUserAboutStreamQuality"
import { rtmpStreamsAtom } from "../../../recoil/atoms/rtmpStreams"
import { showNoVideoSubscribeScreenSelector } from "../../../recoil/atoms/rtc"
import { ShareLinksModal } from "../../mediaPanel/share-links-modal"
import ProductPageOverlay from "../../servicesPanel/badges/product-selector/productPageOverlay"
import { AppStatus, ElementId } from "../../../constants/enums"
import { whiteboardShareUserAtom } from "../../../recoil/atoms/whiteboard"
import { SafeArea } from "src/components/common/SafeArea"

const MeetOnline = window.MeetOnline
const UserType = window.MeetOnline?.UserType

export const MeetingRoom = () => {
  const { screenSize } = useWindowSize()
  const device = detectDeviceType()
  const [isTabletFullScreen] = useRecoilState(
    fullscreenState.isTabletFullScreen
  )
  const setIsMobileDevice = useSetRecoilState(layoutStates.isMobileDevice)
  const setIsTabletDevice = useSetRecoilState(layoutStates.isTabletDevice)

  const isMobile = React.useMemo(
    () =>
      USE_MOBILE_UI || // Fix the layout to mobile view
      isTabletFullScreen ||
      (device !== "Desktop" &&
        device !== "AndroidTab" &&
        device !== "ChromeDesktop" &&
        device !== "IOSIpadChrome" &&
        isIpad() === false &&
        !(device === "IOSChrome" && screenSize.width >= 1024)) ||
      /* (isMobileDevice(screenSize) &&
        screenSize?.width > 0 &&
        device !== "IOSIpadChrome" &&
        device !== "AndroidTab" &&
        screenSize?.height > 0 &&
        isIpad() === false) || */
      (device === "AndroidTab" && screenSize.width < 600),
    [device, screenSize, isTabletFullScreen]
  )

  const isTablet = React.useMemo(
    () => screenSize.width < 768,
    [screenSize.width]
  )

  // Hook to set is mobile value
  React.useEffect(() => {
    setIsMobileDevice(isMobile)
  }, [isMobile])

  // Hook to set is mobile value
  React.useEffect(() => {
    setIsTabletDevice(isTablet)
  }, [isTablet])

  const roomStyles = useRecoilValue(brandingSelector)
  const isStopClientExpanding = useRecoilValue(stopClientExpandingAtom)
  const setMaxHeightAndWidth = !USE_MOBILE_UI && isStopClientExpanding

  const room = useRecoilValue(roomSelector)
  const meetingClient = React.useContext(SDKContext)
  const [showHeader, setShowHeader] = useRecoilState(layoutStates.showHeader)
  const _remoteUsersList = useRecoilValue(remoteUsersListSelector)
  const watchPartySrc = useRecoilValue(watchPartyScreenLinkSelector)
  const isWP = useRecoilValue(watchPartyScreenSelector)
  const showAvatar = roomStyles.enableDigitalAvatar
  const isWatchParty = isWP && !showAvatar

  const watchPartyHostId = useRecoilValue(watchPartyHostIdSelector)
  const _screenShareUser = useRecoilValue(screenShareUserSelector)
  const whiteboardShareUser = useRecoilValue(whiteboardShareUserAtom)
  const rtmpStreams = useRecoilValue(rtmpStreamsAtom)
  const presentMainWindowUser = useRecoilValue(mainWindowUserSelector)
  let _presentMainWindowUser = presentMainWindowUser.uid
  const isAudioWatchParty = React.useMemo(
    () => (watchPartySrc && watchPartySrc.endsWith(".mp3") ? true : false),
    [watchPartySrc]
  )
  const _localUserId = useRecoilValue(localUserIdAtom)
  const [_activeSpeaker, setActiveSpeaker] = useRecoilState(
    activeSpeakerSelector
  )
  const setMainWindowUser = useSetRecoilState(mainWindowUserSelector)
  const [_usersListSmallWindow, setUsersListSmallWindow] = useRecoilState(
    usersListSmallWindowSelector
  )
  const _localUser = useRecoilValue(usersSelector(_localUserId))
  const showNoVideoSubscribeScreen = useRecoilValue(
    showNoVideoSubscribeScreenSelector
  )
  const [targetTabIndex] = useRecoilState<number>(
    layoutStates.historyTabEnabled
  )
  const isShowsGridViewSupported = useRecoilValue(
    isShowsGridViewSupportedSelector
  )

  const isShowTabActive =
    targetTabIndex === SHOW_DESKTOP_TAB_INDEX && isShowsGridViewSupported

  // show toast message to user when downlink and uplink has problem
  useNotifyUserAboutStreamQuality()

  const history = useHistory()
  /** Leave the meeting, when user click browser back button */
  React.useEffect(() => {
    const unlisten = history.listen((_, action) => {
      if (action === "POP") {
        // The user clicked the browser's back button
        meetingClient.disconnectGateway(AppStatus.USER_LEAVE) // leave meeting
      }
    })

    return () => {
      // Clean up the event listener when the component unmounts
      unlisten()
    }
  }, [history])

  React.useEffect(() => {
    // _remoteUsersList doesn't include local user
    let smallWindowUserAdd: string = ""
    const sortedRemoteUsers = [..._remoteUsersList].sort(
      Util.previousSpeakerSorter
    )

    // Set MAIN-WINDOW-USER - START
    let mainWindowUserId = ""

    if (showNoVideoSubscribeScreen) {
      // check MNP image is present. if yes show else background color
      if (room?.branding?.modNotPresentImage) {
        mainWindowUserId = "wpmodNotPresentImage"
        MeetOnline?.client().rtc?.toggleWatchParty(
          room?.branding?.modNotPresentImage,
          "modNotPresentImage"
        )
      } else {
        mainWindowUserId = "wpmodNotPresentOverlay"
        MeetOnline?.client().rtc?.toggleWatchParty(
          "noSource",
          "modNotPresentOverlay"
        )
      }
    } else if (
      !isAudioWatchParty &&
      isWatchParty &&
      watchPartyHostId !== "modNotPresentVideo"
    ) {
      // if watchparty is enabled, sharer should be visible in small window only if his CAM/MIC is/are ON
      mainWindowUserId = "wp" + watchPartyHostId
    } else if (_screenShareUser) {
      // if screenshare is enabled, sharer should be visible in small window only if his CAM/MIC is/are ON
      mainWindowUserId = _screenShareUser.uid
      // console.log(`MAINWINDOWUSER: SCREENSHARE ${mainWindowUserId}`)
    } else if (whiteboardShareUser) {
      mainWindowUserId = whiteboardShareUser.uid
      // console.log(`MAINWINDOWUSER: WHITEBOARD ${mainWindowUserId}`)
    } else if (rtmpStreams.length > 0) {
      // if rtmp stream is active show it on mainWindow
      mainWindowUserId = rtmpStreams[0].uid
    } else if (isAudioWatchParty) {
      mainWindowUserId = watchPartyHostId

      if (!watchPartyHostId) {
        if (sortedRemoteUsers.length > 0) {
          mainWindowUserId = sortedRemoteUsers[0]["id"]
        } else {
          mainWindowUserId = _localUserId
        }
      }
    } else {
      if (sortedRemoteUsers.length > 0) {
        mainWindowUserId = sortedRemoteUsers[0]["id"]
      } else {
        mainWindowUserId = _localUserId
        // console.log(`MAINWINDOWUSER: LOCALUSER ${mainWindowUserId}`)
      }
    }

    // if(_activeSpeaker.uid === "" || _activeSpeaker.uid === 'undefined' || _remoteUsersList.length < 2){
    if (_activeSpeaker.uid === "" || _activeSpeaker.uid === "undefined") {
      setActiveSpeaker({ uid: mainWindowUserId })
    }

    setMainWindowUser({
      uid: mainWindowUserId
    })

    // set main window user id to be used in RTC.js
    meetingClient?.setMainWindowUserId(mainWindowUserId)

    if (
      mainWindowUserId === "wpmodNotPresentImage" ||
      mainWindowUserId === "wpmodNotPresentVideo" ||
      mainWindowUserId === "wpmodNotPresentOverlay" ||
      showNoVideoSubscribeScreen
    ) {
      // If MNP is presenting then there will be no small window users
      setUsersListSmallWindow([])
      return
    }

    // ---------------------For small window users--------------------------------
    if (sortedRemoteUsers.length > 0 || rtmpStreams.length > 0) {
      let otherUserIds = [
        ...sortedRemoteUsers.map(el => el.id),
        ...rtmpStreams?.map(el => el.uid)
      ]

      if (_usersListSmallWindow.length > 0 && otherUserIds.length) {
        // combine user list for small windows with new list (otherUserIds)
        _usersListSmallWindow
          .slice()
          .reverse()
          .forEach(function (item) {
            const index = otherUserIds.indexOf(item)
            if (index > -1) {
              // remove from otherUserIds if already exist
              otherUserIds.splice(index, 1)

              // Insert in otherUserIds array at 0 index
              otherUserIds.unshift(item)
            }
          })
      }

      // remove 1 or more occurences of main window user and local user from list if exist
      const otherUserIdsWithoutMainWindowUserAndLocalUser = otherUserIds.filter(
        element => element !== mainWindowUserId && element !== _localUserId
      )
      otherUserIds = otherUserIdsWithoutMainWindowUserAndLocalUser

      // if (
      //   mainWindowUserId !== _localUserId &&
      //   (_localUser?.camEnabled === true || _localUser?.micEnabled === true)
      // ) {
      //   otherUserIds.unshift(_localUserId)
      // }

      //  add limit here
      if (_localUser?.type === UserType?.MODERATOR) {
        otherUserIds = otherUserIds.slice(
          0,
          RemoteVideoWindowsLimit.FORMODERATORS
        )
      } else if (_localUser?.type === UserType?.BROADCASTER) {
        otherUserIds = otherUserIds.slice(
          0,
          RemoteVideoWindowsLimit.FORBROADCASTERS
        )
      } else if (_localUser?.type === UserType?.AUDIENCE) {
        // if ((isWatchParty && watchPartyHostId !== 'modNotPresentVideo') || _screenShareUser) {
        otherUserIds = otherUserIds.slice(
          0,
          MeetOnline?.client().maxHostsVisibleToAudience
            ? MeetOnline?.client().maxHostsVisibleToAudience - 1
            : 4
        )
      }

      // Local video should be visible irrespective to setting
      if (
        mainWindowUserId !== _localUserId &&
        (_localUser?.camEnabled === true || _localUser?.micEnabled === true)
      ) {
        otherUserIds.unshift(_localUserId)
      }

      if (
        _presentMainWindowUser !== "" &&
        _presentMainWindowUser !== sortedRemoteUsers?.[0]?.["id"]
      ) {
        smallWindowUserAdd = _presentMainWindowUser
      }

      let isSmallWindowUserAlreadyExist = 0
      // check if user to be added in small windows already exist or not

      if (smallWindowUserAdd && smallWindowUserAdd !== _localUserId) {
        for (let [, value] of Object.entries(otherUserIds)) {
          if (value === smallWindowUserAdd) {
            isSmallWindowUserAlreadyExist = 1
            break
          }
        }
      }

      // if previous main window user doesn't already exist in small windows, then add it
      if (!isSmallWindowUserAlreadyExist && !isWatchParty) {
        const smallWindowUserIdsWithoutLocalUser = otherUserIds.filter(
          uid => uid !== _localUserId
        )
        const usersDataList = sortedRemoteUsers.filter(
          singleUser =>
            smallWindowUserIdsWithoutLocalUser.indexOf(singleUser.id) !== -1
        )
        const RVsortedUsers = [...usersDataList].sort(
          Util.sortUsersByLastSpeakTime
        )

        if (
          RVsortedUsers.length &&
          sortedRemoteUsers.indexOf(smallWindowUserAdd) > -1
        ) {
          const userToBeReplaced = RVsortedUsers[RVsortedUsers.length - 1]["id"]
          const newOtherUserIds = otherUserIds.map(sId =>
            sId === userToBeReplaced ? smallWindowUserAdd : sId
          )
          otherUserIds = newOtherUserIds
        }
      }

      if (
        JSON.stringify(otherUserIds) !== JSON.stringify(_usersListSmallWindow)
      ) {
        setUsersListSmallWindow(otherUserIds)
      } else if (otherUserIds.length === 0) {
        setUsersListSmallWindow([])
      }
    } else {
      let otherUserIds = []
      if (
        (!isAudioWatchParty &&
          isWatchParty &&
          watchPartyHostId !== "modNotPresentVideo") ||
        _screenShareUser ||
        whiteboardShareUser ||
        rtmpStreams.length > 0
      ) {
        // If Wp and screen is shared by local user and local user's cam/mic is on, then show local user in small window for himself, others will see hin as he will be treated as remote users for others
        const madiaSharerUserId = mainWindowUserId.slice(2)
        // console.log(`madiaSharerUserId: ${madiaSharerUserId}`)
        if (
          madiaSharerUserId === _localUserId &&
          (_localUser?.camEnabled === true || _localUser?.micEnabled === true)
        ) {
          otherUserIds.push(madiaSharerUserId)
        } else if (
          madiaSharerUserId !== _localUserId &&
          (_localUser?.camEnabled === true || _localUser?.micEnabled === true)
        ) {
          otherUserIds.push(_localUserId)
        }
      }
      // console.log(`otherUserIds: ${otherUserIds}`)
      setUsersListSmallWindow(otherUserIds)
    }
    // console.log("whiteboardShareUser:", whiteboardShareUser)
  }, [
    _remoteUsersList,
    isWatchParty,
    _localUser,
    _screenShareUser,
    watchPartyHostId,
    isAudioWatchParty,
    rtmpStreams,
    showNoVideoSubscribeScreen,
    whiteboardShareUser
  ])

  React.useEffect(() => {
    if (isMobile || isTablet) {
      setShowHeader(false)
    } else if (room?.enableHeaderBar === false) {
      setShowHeader(false)
    } else {
      setShowHeader(true)
    }
  }, [isMobile, room?.enableHeaderBar, isTablet])

  return (
    <SafeArea
      background={
        roomStyles?.roomBackgroundColor
          ? roomStyles.roomBackgroundColor
          : "gray.100"
      }
    >
      <Flex
        height={showHeader ? "calc(100% - 3rem)" : "100%"}
        w="100%"
        boxShadow={
          !isMobile && !isTablet && "inset 0 0 0 10000px rgba(0, 0, 0, 0.025)"
        }
        justifyContent="center"
        alignItems="center"
        bgColor={
          roomStyles?.roomBackgroundColor
            ? roomStyles.roomBackgroundColor
            : "gray.100"
        }
        /*  bgImage={
        roomStyles?.roomBackgroundImage
          ? `url(${roomStyles.roomBackgroundImage})`
          : "none"
      } */
        backgroundPosition="center"
        bgSize="cover"
        pos="relative"
      >
        {isShowTabActive && (
          <Box
            zIndex={MODAL_Z_INDEX}
            pos="absolute"
            display="flex"
            justifyContent={!setMaxHeightAndWidth ? "center" : "unset"}
            top="0px"
            left={
              !setMaxHeightAndWidth
                ? { base: "0px" }
                : { base: "0px", "2xl": "unset" }
            }
            width={{ base: "100%", md: "calc(100% - 75px)" }}
            height="100%"
            maxWidth={
              setMaxHeightAndWidth && {
                base: "1625px",
                "2xl": "calc(1600px - 75px)"
              }
            }
            marginLeft={setMaxHeightAndWidth && { "2xl": "-75px" }}
            maxH={setMaxHeightAndWidth && "1205px"}
          >
            <Shows />
          </Box>
        )}

        <Flex
          flex={1}
          overflow="hidden"
          height="100%"
          maxWidth={setMaxHeightAndWidth && "1600px"}
          maxH={setMaxHeightAndWidth && "1205px"}
          bgColor={
            /*   !isMobile && !isTablet && */

            roomStyles?.roomBackgroundColor
              ? roomStyles.roomBackgroundColor
              : "gray.100"
          }
          bgImage={
            roomStyles?.roomBackgroundImage
              ? `url(${roomStyles.roomBackgroundImage})`
              : "none"
          }
          backgroundPosition="center"
          bgSize="cover"
          className="mobileIOSHedear"
          pos="relative"
        >
          <div style={{ position: "fixed", zIndex: -1, width: "1px" }}>
            <video id={ElementId.PIP_VIDEO} playsInline muted autoPlay />
          </div>
          {isMobile ? (
            <SmallView />
          ) : (
            <>
              <Flex
                flexDirection="column"
                flex={1}
                p={!isTablet && "4"}
                bgSize="cover"
                overflow="hidden"
              >
                <MediaPanel />
                <Flex pos="absolute" bottom="0px" left="0px"></Flex>
              </Flex>
              {!isTablet && (
                <Box
                  maxHeight={showHeader ? "calc(100vh - 3rem)" : "100vh"}
                  p="0"
                >
                  <ServicesPanel />
                  <HandleChatCount />
                </Box>
              )}
            </>
          )}
          <LSRTCMediaConnection />
          <PrivateRoomHandler />
          <ShareLinksModal isTablet={isTablet} isMobile={isMobile} />
          <ProductPageOverlay />
        </Flex>
      </Flex>
    </SafeArea>
  )
}
export default MeetingRoom
